import type { OpenFeatureSupportedFlags } from '../../openfeature/types';
import type { FlagKey } from './types';

export const flags = {
  'is-education-plans-routing': {
    enabled: [true, false],
  },
  'live-editor-csat': {
    enabled: [true, false],
  },
  'live-linear-navigation': {
    enabled: [true, false],
  },
  'nux-forceful-editor-tour': {
    enabled: [true, false],
  },
  'nux-mobile-intent-onboarding': {
    enabled: [true, false],
  },
  'nux-backfilling-onboarding-data': {
    enabled: [true, false],
  },
  'idx-learning-management-integration': {
    enabled: [true, false],
  },
  'ins-results-ai-summary': {
    enabled: [true, false],
  },
  'og-homepage-personalized-header': {
    enabled: [true, false],
  },
  'og-google-signup': {
    version: ['v1', 'v2'],
  },
  'pc-interactive-canvas-editing': {
    enabled: ['all-slide-types', 'off'],
  },
  'pc-throw-on-missing-interactive-content': {
    enabled: [true, false],
  },
  'cx-hide-deprecated-slide-types': {
    enabled: [true, false],
  },
  'cx-pyne-onboarding': {
    enabled: [true, false],
  },
  'pay-discount-badges-cbc': {
    enabled: [true, false],
  },
  'pay-upsell-toggles-cbc': {
    enabled: [true, false],
  },
  'pay-purchase-to-upgrade': {
    enabled: [true, false],
  },
  'biz-promote-enterprise-banner': {
    enabled: [true, false],
  },
  'pug-test-server-side-rendering': {
    heading: ['Real heading', 'Fake heading'],
    font_size: [1000, 0],
    enabled: [true, false],
  },
  'ai-conv-ui': {
    version: ['v1', 'feature-at-capacity'],
  },
  'ai-conv-ui-file-upload': {
    enabled: [true, false],
  },
  'glab-templates-modal-redesign': {
    enabled: [true, false],
  },
  'glab-preferred-getting-started': {
    enabled: [true, false],
  },
  'wsxp-leave-workspace-setting': {
    enabled: [true, false],
  },
  'pi-qr-code-new-look': {
    enabled: [true, false],
  },
} as const satisfies OpenFeatureSupportedFlags;

export const flagsMetadata: Record<
  FlagKey,
  { description: string; displayName: string }
> = {
  'is-education-plans-routing.enabled': {
    displayName: 'PAY02 - Education plans routing',
    description:
      'Route to education plans when knowing that the user is an educational user',
  },
  'live-editor-csat.enabled': {
    displayName: 'LIVE - Show CSAT Form',
    description: 'Show the CSAT form to the user in the editor.',
  },
  'live-linear-navigation.enabled': {
    displayName: 'LIVE - Linear Navigation',
    description: 'Show new linear navigation features',
  },
  'nux-forceful-editor-tour.enabled': {
    displayName: 'NUX - Forceful Editor Tour',
    description: 'Show a forceful tour in the editor',
  },
  'nux-mobile-intent-onboarding.enabled': {
    displayName: 'NUX - Mobile Intent Onboarding',
    description: 'Enable mobile intent onboarding for new users',
  },
  'nux-backfilling-onboarding-data.enabled': {
    displayName: 'NUX - Backfilling onboarding data',
    description: 'Backfill onboarding data in the dashboard for existing users',
  },
  'idx-learning-management-integration.enabled': {
    displayName: 'Show LMS integration',
    description: 'Show the LMS integration in the workspace setting',
  },
  'ins-results-ai-summary.enabled': {
    displayName: 'INS - Results AI Summary',
    description: 'Show the AI summary in the results page',
  },
  'og-homepage-personalized-header.enabled': {
    displayName: 'OG - Homepage Personalized Header',
    description: 'Show a personalized header on the homepage for executives',
  },
  'og-google-signup.version': {
    displayName: 'OG - Downprioritize Google Signup',
    description: 'Downprioritize Google Signup experiment in the signup flow',
  },
  'pc-interactive-canvas-editing.enabled': {
    displayName: 'PC - Interactive canvas editing',
    description:
      'Enable interactive canvas editing prior to simplifying the editor',
  },
  'pc-throw-on-missing-interactive-content.enabled': {
    displayName: 'PC - Throw on missing interactive content',
    description: 'Throw an error when an interactive content is missing',
  },
  'cx-hide-deprecated-slide-types.enabled': {
    displayName: 'CX - Hide deprecated slide types',
    description: 'Hide deprecated slide types in the add slide popover',
  },
  'cx-pyne-onboarding.enabled': {
    displayName: 'CX - Pyne.ai onboarding',
    description: 'Enable AI onboarding through Pyne.ai',
  },
  'pay-discount-badges-cbc.enabled': {
    displayName: 'PAY - Discount Badges in CBC',
    description: 'Show discount badges in the custom built checkout',
  },
  'pay-upsell-toggles-cbc.enabled': {
    displayName: 'PAY - Upsell toggles in CBC',
    description: 'Show upsell toggles in the custom built checkout',
  },
  'pay-purchase-to-upgrade.enabled': {
    displayName: 'PAY - Purchase to upgrade',
    description:
      'A flow for purchasing a new license and upgrade to member in one click',
  },
  'biz-promote-enterprise-banner.enabled': {
    displayName: 'BIZ - Promote Enterprise Banner',
    description:
      'Show the enterprise banner in the dashboard for organization leaders',
  },
  'pug-test-server-side-rendering.heading': {
    displayName: 'PUG - Test server side rendering',
    description: 'Test server side rendering',
  },
  'pug-test-server-side-rendering.font_size': {
    displayName: 'PUG - Test server side rendering',
    description: 'Test server side rendering',
  },
  'pug-test-server-side-rendering.enabled': {
    displayName: 'PUG - Test server side rendering',
    description: 'Test server side rendering',
  },
  'ai-conv-ui.version': {
    displayName: 'AI - Conversational UI',
    description: 'Show the conversational UI in the dashboard',
  },
  'ai-conv-ui-file-upload.enabled': {
    displayName: 'AI - Conversational UI file upload feature',
    description:
      'Enable file upload button on the converssational ui chat input',
  },
  'glab-templates-modal-redesign.enabled': {
    displayName: 'GLAB - Templates modal redesign',
    description: 'Enables the templates modal redesign.',
  },
  'glab-preferred-getting-started.enabled': {
    displayName: 'GLAB - Preferred Getting Started Step',
    description:
      'Enable the new preferred getting started step in onboarding flow',
  },
  'wsxp-leave-workspace-setting.enabled': {
    displayName: 'WSXP - Leave workspace from settings',
    description: 'Enable the leave workspace section in user account settings',
  },
  'pi-qr-code-new-look.enabled': {
    displayName: '🍰 - QR Code New Look',
    description:
      'Have a QR code on the side so it does not cover the slide content',
  },
};
